import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'

import { CurriculumRouteContentGetter as RouteContentGetter } from 'layers/content/v3/Hocs/CurriculumRouteContentGetter'
import withApiError from 'layers/errorHandling/apiError'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import { parseGradeAndProgram } from 'utils/lmsHelpers'
import {
  resolveRouteServiceApp,
  resolveCurriculumPreviewMode,
} from 'utils/routing/routeHelpers'
import { TICourseNameToProgramMap, K5_PROGRAM_TYPE } from 'utils/program/types'

import { useUnitLessonCompletion } from 'hooks/useMarkCompleteV3'
import {
  resolveCourseCode,
  selectClassRegistrationFromResponse,
} from 'pages/UnitV3/data'

import {
  findActiveHeader,
  prepareDataForHeaderTabs,
  prepareItemsForShutterList,
  generateProgressText,
  formatCourseDisplayTitle,
} from './data'
import {
  handleBackClick,
  handleHeaderTabChange,
  handleTabChange,
} from './helper'
import { TieredInterventionsUnitsPageComponent } from './component'
import { PAGE_TITLE, BACK_LINK_ELEMENTARY, BACK_LINK_MIDDLE } from './constants'

/**
 * Maps curriculum-service contentGroupCode values to the corresponding
 * top-level key in the /v2/instructor/me response.
 */
const CONTENT_GROUP_TO_INSTRUCTOR_KEY = {
  k5seldigital: 'K5SelDigital',
  mspdigital: 'MspDigital',
}

/**
 * Tier-2 unit pages fetched via curriculum-service v3.
 *
 * Replaces the legacy FlaggedRouteContentGetter + FlaggedCourseManagerGetter
 * stack with CurriculumRouteContentGetter and useUnitLessonCompletion so
 * page content and course-tree completion both come from v3.
 */
export function TieredInterventionsContainer(props) {
  const {
    match,
    location,
    navigationEvent,
    routeContent,
    instructorData,
    currentSite,
  } = props || {}
  const { url: currentUrl } = match || {}

  const { content = {}, loading, route, contentGroupCode, coursePointer } =
    routeContent || {}
  const queryType = route?.queryType
  const unitTi =
    (queryType && content?.[queryType]) || content?.unitTi || content?.unit

  const { linkSummaries: baseLinkSummaries = [], tabSummaries, courseSummary } =
    unitTi || {}
  const { displayTitle } = courseSummary || {}

  const classId = useMemo(() => {
    const params = new URLSearchParams(location?.search || '')
    return params.get('class') ?? undefined
  }, [location?.search])

  const { grade: tier2GradeKey } = parseGradeAndProgram(match?.url) || {}
  const courseCode = resolveCourseCode({
    coursePointer,
    unit: unitTi,
    gradeSegment: tier2GradeKey,
    contentGroupCode,
  })

  const instructorDataKey =
    CONTENT_GROUP_TO_INSTRUCTOR_KEY[contentGroupCode?.toLowerCase()] || null

  const presentationMode = match?.params?.verb
  const isTeachRoute = presentationMode === 'teach'

  // Completion and /v2/instructor/me are only relevant in teach mode — mirror
  // the legacy flag.off behaviour and UnitV3's preview guard.
  const completionInstructorDataKey = isTeachRoute ? instructorDataKey : null

  const {
    linkSummaries: updatedSummaries,
    isLoading: isCompletionLoading,
  } = useUnitLessonCompletion({
    instructorDataKey: completionInstructorDataKey,
    linkSummaries: baseLinkSummaries,
    siteId: currentSite?.siteId,
    gradeSegment: tier2GradeKey,
    courseCode,
    coursePointer,
    contentGroupCode,
  })

  const selectedClass = useMemo(() => {
    if (!isTeachRoute || !instructorData) return null

    return selectClassRegistrationFromResponse({
      instructorResponse: instructorData,
      classId,
      siteId: currentSite?.siteId,
      gradeSegment: tier2GradeKey,
      courseCode,
    })
  }, [
    isTeachRoute,
    instructorData,
    classId,
    currentSite?.siteId,
    tier2GradeKey,
    courseCode,
  ])

  const isInstructorDataLoading =
    isTeachRoute &&
    (instructorDataKey
      ? isCompletionLoading
      : !instructorData || Object.keys(instructorData).length === 0)

  const readyToLoad =
    !loading &&
    !!unitTi &&
    !isInstructorDataLoading &&
    (!isTeachRoute || !!selectedClass)

  const activeHeaderTab = findActiveHeader(tabSummaries)
  const headerTabs = prepareDataForHeaderTabs({ tabSummaries, activeHeaderTab })
  const items = prepareItemsForShutterList(updatedSummaries)

  const declareTabChange = handleTabChange({ location, navigationEvent })
  const declareHeaderTabChange = handleHeaderTabChange({
    tabSummaries,
    handleTabChange: declareTabChange,
  })

  const progressText = generateProgressText({
    currentUrl,
    ourClass: selectedClass,
  })

  const categoryTitle = activeHeaderTab?.title || PAGE_TITLE

  useEffect(() => {
    if (categoryTitle) {
      document.title = categoryTitle
    }
  }, [categoryTitle])

  const { grade: courseType } = parseGradeAndProgram(match?.url) || {}
  const readableProgramType = TICourseNameToProgramMap?.[courseType]

  const declareBackClick = handleBackClick({ navigationEvent, courseType })
  const backLinkText =
    readableProgramType === K5_PROGRAM_TYPE
      ? BACK_LINK_ELEMENTARY
      : BACK_LINK_MIDDLE
  const isPreview = resolveCurriculumPreviewMode({ match, location })

  const courseDisplayTitle = formatCourseDisplayTitle({
    displayTitle,
    selectedClass,
  })

  return (
    readyToLoad && (
      <TieredInterventionsUnitsPageComponent
        activeHeaderTab={activeHeaderTab}
        backLinkText={backLinkText}
        content={content}
        courseDisplayTitle={courseDisplayTitle}
        handleBackClick={declareBackClick}
        handleHeaderTabChange={declareHeaderTabChange}
        handleTabChange={declareTabChange}
        headerTabs={headerTabs}
        isPreview={isPreview}
        items={items}
        linkSummaries={updatedSummaries}
        location={location}
        match={match}
        navigationEvent={navigationEvent}
        ourClass={selectedClass}
        presentationMode={presentationMode}
        programType={readableProgramType}
        progressText={progressText}
      />
    )
  )
}

TieredInterventionsContainer.propTypes = {
  currentSite: PropTypes.shape({
    siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  instructorData: PropTypes.object,
  location: PropTypes.object,
  match: PropTypes.object,
  navigationEvent: PropTypes.func,
  routeContent: PropTypes.object,
}

const options = {
  app: ({ route }) => resolveRouteServiceApp(route),
  routeResolver: () => window?.location?.pathname,
}

export const TieredInterventionsUnitsPage = withSiteContext(
  RouteContentGetter(
    withApiError(withRouter(TieredInterventionsContainer)),
    options,
  ),
)

export default TieredInterventionsUnitsPage
